"use client";

import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import Icon from "../../atoms/Icon";
import VideoIframe from "../../atoms/VideoIframe";
import { ICONS } from "../../variables/icons";

interface CardVideoModalProps {
  videoUrl: string;
  isPreviewMode: boolean;
  onClose: () => void;
  getIsModalOpen: () => boolean;
}

const CardVideoModal = ({
  videoUrl,
  onClose,
  getIsModalOpen,
  isPreviewMode,
}: CardVideoModalProps) => {
  const modalRef = useRef<HTMLDialogElement | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const modalElement = modalRef.current;
    const htmlElement = document.documentElement;

    if (modalElement) {
      if (getIsModalOpen()) {
        // Mirror the open prop into local animation state alongside the
        // imperative <dialog> API call; both must happen together.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsAnimating(true);
        modalElement.showModal();
        htmlElement.classList.add("no-scroll");
        modalElement.focus();
      } else {
        htmlElement.classList.remove("no-scroll");
        modalElement.close();
      }
    }
  }, [getIsModalOpen]);

  useEffect(() => {
    const modalElement = modalRef.current;
    const htmlElement = document.documentElement;
    if (modalElement) {
      if (getIsModalOpen()) {
        modalElement.showModal();
        htmlElement.classList.add("no-scroll");
        modalElement.focus();
      } else {
        htmlElement.classList.remove("no-scroll");
        modalElement.close();
      }
    }

    return () => {
      htmlElement.classList.remove("no-scroll");
    };
  }, [getIsModalOpen]);

  const handleCloseModal = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === "Escape") {
      handleCloseModal();
    }
  };

  return (
    <div className="modal-component" data-role="modal-component">
      <dialog
        ref={modalRef}
        data-testid="modal-dialog"
        className={`fixed z-50 w-[90%] h-full max-h-[620px] max-w-[900px] top-[10px] left-1/2 -translate-x-1/2 ${
          isAnimating
            ? "translate-y-[10px] opacity-100"
            : "-translate-y-full opacity-0"
        } rounded-md shadow-lg border-none outline-none transition-all duration-600 ease-in-out`}
        aria-modal="true"
        role="dialog"
        aria-labelledby="modal-title"
        onKeyDown={handleKeyDown}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleCloseModal();
          }
        }}
        id="viddeo-modal"
      >
        <div className="max-h-[80vh] overflow-y-auto">
          <div className="bg-white relative rounded-md">
            <div className="flex border-b border-mcclaren-modal-divider p-[15px] justify-end">
              <button
                data-testid="close-button"
                className="text-black hover:text-gray-600 cursor-pointer"
                aria-label="Close modal"
                onClick={handleCloseModal}
              >
                <Icon icon={ICONS.DISMISS} />
              </button>
            </div>
            <div
              className="relative border-b border-mcclaren-modal-divider p-[15px]"
              data-testid="modal-content"
            >
              <div className="rounded-md overflow-hidden relative flex mb-[15px]">
                <VideoIframe
                  src={videoUrl}
                  className=""
                  isPreviewMode={isPreviewMode}
                />
              </div>
            </div>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default CardVideoModal;
